/* eslint-disable jsx-a11y/anchor-is-valid */
import {FormEvent, useEffect, useRef, useState} from 'react'
import clsx from 'clsx'
import {
  formatBackupCodeInput,
  isCompleteBackupCode,
  isCompleteTotp,
  sanitizeTotpInput,
  TOTP_LENGTH,
} from '../mfa/mfaCodes'

type Mode = 'totp' | 'backup'

interface Props {
  /** A request is in flight — inputs and buttons are disabled. */
  submitting: boolean
  error: string | null
  /** Bumps on every failed request; used to clear the input even if the message repeats. */
  errorSeq: number
  /** Called automatically as soon as 6 digits are entered (and on Enter). */
  onSubmitTotp: (code: string) => void
  /** Called on Enter / button for a complete `XXXX-XXXX` backup code. */
  onSubmitBackupCode: (code: string) => void
  onBack: () => void
}

/**
 * Step 2 of the sign-in for an admin who already has TOTP enabled.
 * 6-digit numeric field with auto-submit, plus a "Use a backup code" alternative.
 */
export function MfaCodeStep({submitting, error, errorSeq, onSubmitTotp, onSubmitBackupCode, onBack}: Props) {
  const [mode, setMode] = useState<Mode>('totp')
  const [value, setValue] = useState('')
  const [localError, setLocalError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const lastAutoSubmitted = useRef<string | null>(null)

  // After any failed request: clear the field and refocus so the user can retype immediately.
  useEffect(() => {
    if (errorSeq > 0) {
      setValue('')
      lastAutoSubmitted.current = null
    }
  }, [errorSeq])

  // Focus the code field on mount and whenever a request settles (the field is disabled while
  // a request is in flight, so focusing at that moment would be a no-op).
  useEffect(() => {
    if (!submitting) inputRef.current?.focus()
  }, [submitting])

  useEffect(() => {
    inputRef.current?.focus()
  }, [mode])

  const switchMode = (next: Mode) => {
    if (submitting) return
    setMode(next)
    setValue('')
    setLocalError(null)
    lastAutoSubmitted.current = null
  }

  const handleTotpChange = (raw: string) => {
    const next = sanitizeTotpInput(raw)
    setValue(next)
    setLocalError(null)
    if (isCompleteTotp(next) && !submitting && lastAutoSubmitted.current !== next) {
      lastAutoSubmitted.current = next
      onSubmitTotp(next)
    }
  }

  const handleBackupChange = (raw: string) => {
    setValue(formatBackupCodeInput(raw))
    setLocalError(null)
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (submitting) return
    if (mode === 'totp') {
      if (!isCompleteTotp(value)) {
        setLocalError(`Enter the ${TOTP_LENGTH}-digit code from your authenticator app.`)
        return
      }
      lastAutoSubmitted.current = value
      onSubmitTotp(value)
    } else {
      if (!isCompleteBackupCode(value)) {
        setLocalError('Enter one of your backup codes in the format XXXX-XXXX.')
        return
      }
      onSubmitBackupCode(value)
    }
  }

  const shownError = localError || error

  return (
    <form className='form w-100' onSubmit={handleSubmit} noValidate id='kt_login_mfa_form'>
      {/* begin::Heading */}
      <div className='text-center mb-11'>
        <h1 className='text-dark fw-bolder mb-3'>Two-step verification</h1>
        <div className='text-gray-500 fw-semibold fs-6'>
          {mode === 'totp'
            ? 'Enter the 6-digit code from your authenticator app.'
            : 'Enter one of the backup codes you saved when you set up two-step verification. Each code works once.'}
        </div>
      </div>
      {/* end::Heading */}

      {shownError && (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>{shownError}</div>
        </div>
      )}

      {/* begin::Form group */}
      <div className='fv-row mb-8'>
        <label className='form-label fs-6 fw-bolder text-dark' htmlFor='kt_login_mfa_code'>
          {mode === 'totp' ? 'Authenticator code' : 'Backup code'}
        </label>
        {mode === 'totp' ? (
          <input
            id='kt_login_mfa_code'
            ref={inputRef}
            type='text'
            inputMode='numeric'
            pattern='[0-9]*'
            autoComplete='one-time-code'
            maxLength={TOTP_LENGTH}
            placeholder='123456'
            value={value}
            disabled={submitting}
            onChange={(e) => handleTotpChange(e.target.value)}
            className={clsx('form-control bg-transparent text-center fs-2 fw-bold', {
              'is-invalid': !!shownError,
            })}
            style={{letterSpacing: '0.5em'}}
            autoFocus
          />
        ) : (
          <input
            id='kt_login_mfa_code'
            ref={inputRef}
            type='text'
            autoComplete='off'
            autoCapitalize='characters'
            spellCheck={false}
            maxLength={9}
            placeholder='XXXX-XXXX'
            value={value}
            disabled={submitting}
            onChange={(e) => handleBackupChange(e.target.value)}
            className={clsx('form-control bg-transparent text-center fs-2 fw-bold text-uppercase', {
              'is-invalid': !!shownError,
            })}
            style={{letterSpacing: '0.2em'}}
            autoFocus
          />
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Action */}
      <div className='d-grid mb-8'>
        <button
          type='submit'
          id='kt_login_mfa_submit'
          className='btn btn-primary'
          disabled={submitting || (mode === 'totp' ? !isCompleteTotp(value) : !isCompleteBackupCode(value))}
        >
          {!submitting && <span className='indicator-label'>Verify</span>}
          {submitting && (
            <span className='indicator-progress' style={{display: 'block'}}>
              Please wait...
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
      </div>
      {/* end::Action */}

      <div className='d-flex flex-stack flex-wrap gap-3 fs-base fw-semibold'>
        <a
          href='#'
          className='link-primary'
          onClick={(e) => {
            e.preventDefault()
            if (!submitting) onBack()
          }}
        >
          Back to sign in
        </a>
        {mode === 'totp' ? (
          <a
            href='#'
            className='link-primary'
            onClick={(e) => {
              e.preventDefault()
              switchMode('backup')
            }}
          >
            Use a backup code
          </a>
        ) : (
          <a
            href='#'
            className='link-primary'
            onClick={(e) => {
              e.preventDefault()
              switchMode('totp')
            }}
          >
            Use my authenticator app
          </a>
        )}
      </div>
    </form>
  )
}
